import './savedSearchHistoryComponent.scss';
import React, {Fragment, MouseEventHandler, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {SavedSearchWithId} from '../duck/saveSearchComponent-types';
import {useSearchPageType} from '../../search-hooks';
import {
    deleteSavedSearchFolder,
    deleteSavedSearchItem,
    shareSavedSearchItem
} from '../duck/saveSearchComponent-actions';
import {HibuSearchRequest, SortType} from '../../search-types';
import {
    searchResetFacetsAction,
    searchSavedSearch,
    setSearchQueryFieldsAction,
    sortChangeAction
} from '../../duck/search-actions';
import {Dropdown} from 'react-bootstrap';
import {SvgIcon} from '../../../icons/svgIcon';
import {Icons, IconType} from '../../../icons/icons';
import {useStateContainerFacets, useStateContainerName} from '../../../settings/duck/settings-hooks';
import {SearchPageType} from '../../search-config';
import classNames from 'clsx';
import {doFetchFacets} from '../../../services/search-service';
import {VerticalSeparator} from '../../../common/verticalSeparator/verticalSeparator';
import {HibuTooltip} from '../../../common/hibuTooltip/hibuTooltip';


type SavedSearchHistoryProps = {
    savedSearches?: SavedSearchWithId[];
    savedSearchesSharedByOthers?: SavedSearchWithId[];
    savedSearchesSharedByOthersEnabled: boolean;
}

type SavedSearchGroupProps = {
    uniqueName: string;
    savedSearches?: SavedSearchWithId[];
    icon: IconType;
    editionPermission: boolean;
    mayShare: boolean;
    titleKey: string;
    emptyContentKey: string;
}


export const SavedSearchHistory = React.memo(({
                                                  savedSearches,
                                                  savedSearchesSharedByOthers,
                                                  savedSearchesSharedByOthersEnabled
                                              }: SavedSearchHistoryProps) => {
    return <>
        <VerticalSeparator/>
        <SavedSearchGroup uniqueName={'own'} icon={Icons.SEARCH_FOLDER}
                          editionPermission={true}
                          mayShare={savedSearchesSharedByOthersEnabled}
                          savedSearches={savedSearches}
                          titleKey={'search.saveSearch.action.ownSearchTitle'}
                          emptyContentKey={'search.saveSearch.action.ownEmpty'}/>
        {savedSearchesSharedByOthersEnabled && (
            <SavedSearchGroup uniqueName={'shared'} icon={Icons.SHARED_FOLDER}
                              editionPermission={false}
                              mayShare={false}
                              savedSearches={savedSearchesSharedByOthers}
                              titleKey={'search.saveSearch.action.otherSearchTitle'}
                              emptyContentKey={'search.saveSearch.action.sharedEmpty'}/>
        )}
    </>;
});

const SavedSearchGroup = React.memo(({
                                         uniqueName,
                                         savedSearches,
                                         editionPermission = false,
                                         mayShare = false,
                                         icon,
                                         titleKey,
                                         emptyContentKey
                                     }: SavedSearchGroupProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const containerFacets = useStateContainerFacets();
    const containerName = useStateContainerName();
    const [selectedFolder, setSelectedFolder] = useState<string | undefined>(undefined);
    const history = useHistory();

    const isOnTheContainer = useCallback((currentValue: SavedSearchWithId) => {
        return currentValue.request?.container === containerName;
    }, [containerName]);

    const allFolders = useCallback(() => {
        return savedSearches?.reduce((currentList: string[], currentValue: SavedSearchWithId) => {
            if (currentList.includes(currentValue.folder) || !isOnTheContainer(currentValue)) {
                return currentList;
            }
            return [...currentList, currentValue.folder];
        }, []) || [];
    }, [savedSearches, isOnTheContainer]);

    function onSelectSavedSearch(request: HibuSearchRequest) {
        dispatch(searchResetFacetsAction(SearchPageType.BASIC));
        const currentSortType: SortType = {
            field: request.sort.field as string,
            order: request.sort.order as string
        };
        dispatch(sortChangeAction(searchPageType, currentSortType));
        if (request.queryFields) {
            dispatch(setSearchQueryFieldsAction(searchPageType, request.queryFields));
        }
        dispatch(doFetchFacets(searchPageType, request));
        dispatch(searchSavedSearch(searchPageType, request, history, containerFacets));
    }

    function onDeleteSavedSearch(savedSearchId: number) {
        dispatch(deleteSavedSearchItem(searchPageType, savedSearchId));
    }

    function onShareSavedSearch(savedSearchId: number, shared: boolean) {
        dispatch(shareSavedSearchItem(searchPageType, savedSearchId, shared));
    }

    function onDeleteSavedSearchFolder(folderName: string) {
        dispatch(deleteSavedSearchFolder(searchPageType, folderName));
    }

    function isOnTheSelectedFolder(item: SavedSearchWithId) {
        return item.folder === selectedFolder;
    }

    const folderListComponent = (): JSX.Element => {
        return (<div className="saved-search-menu-content">{allFolders().length > 0 ?
            allFolders().map((folder, index) =>
                (
                    <Fragment key={`${folder}-${index}`}>
                        <SavedSearchFolderItem name={folder} editable={editionPermission}
                                               onClick={() => {
                                                   setSelectedFolder(folder);
                                               }}
                                               onDelete={() => onDeleteSavedSearchFolder(folder)}
                        />
                    </Fragment>)
            )
            :
            <div className={'saved-search-menu-content-empty'}>
                {t(emptyContentKey)}
            </div>
        }
        </div>);
    };

    const savedSearchesItemList = (): JSX.Element => {
        return (<div className="saved-search-menu-content">
            {(savedSearches || [])
                .filter(item => isOnTheContainer(item) && isOnTheSelectedFolder(item))
                .map(item => (
                    <Fragment key={item.id}>
                        <SaveSearchItem
                            savedQuery={item}
                            onSelect={onSelectSavedSearch}
                            onDelete={onDeleteSavedSearch}
                            onShared={onShareSavedSearch}
                            editable={editionPermission}
                            mayShare={mayShare}
                        />
                    </Fragment>
                ))}
        </div>);
    };
    const contentHeader = (): JSX.Element => {
        return <Dropdown.Header>
            {selectedFolder &&
                <SvgIcon icon={Icons.ARROW_BACK} id={`icon-search-folder-${uniqueName}`} className={'icon'}
                         onClick={() => setSelectedFolder(undefined)}/>}
            {selectedFolder ? selectedFolder : t(titleKey)}</Dropdown.Header>;
    };


    return (<div id={`saved-search-history-${uniqueName}`} className={`saved-search-history`}>
            <Dropdown id={`saved-search-dropdown-${uniqueName}`}>
                <Dropdown.Toggle id={`saved-search-toggle-${uniqueName}`}>
                    <SvgIcon icon={icon} id={`icon-search-folder-${uniqueName}`}/>
                </Dropdown.Toggle>
                <Dropdown.Menu id={`saved-search-menu-${uniqueName}`} show={false}>
                    {contentHeader()}
                    {selectedFolder ? savedSearchesItemList() : folderListComponent()}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
});

type SavedSearchFolderProps = {
    name: string;
    onClick: MouseEventHandler<HTMLDivElement>;
    editable?: boolean
    onDelete?: MouseEventHandler<HTMLDivElement>;
}

const SavedSearchFolderItem = React.memo(({
                                              name,
                                              onClick,
                                              editable = false,
                                              onDelete
                                          }: SavedSearchFolderProps) => {
        const {t} = useTranslation();

        return (
            <div className={'saved-search-item'}>
                <div className={classNames('saved-search-item-info-wrapper')}>
                    <HibuTooltip tooltipKey={name} placement={'auto'} key={name}>
                        <div className="saved-search-item-link link" onClick={onClick}>{name}</div>
                    </HibuTooltip>
                    {editable && <SvgIcon
                        className="saved-search-icon"
                        icon={Icons.DELETE}
                        onClick={onDelete}
                        alt={t('search.saveSearch.remove')}
                    />}
                </div>
            </div>
        );
    }
);


interface SaveSearchItemProps {
    savedQuery: SavedSearchWithId;
    onSelect: (request: HibuSearchRequest) => void;
    onShared?: (id: number, shared: boolean) => void;
    onDelete?: (id: number) => void;
    editable: boolean;
    mayShare: boolean;
}

export const SaveSearchItem = React.memo(({
                                              savedQuery,
                                              onSelect,
                                              onDelete,
                                              onShared,
                                              editable,
                                              mayShare
                                          }: SaveSearchItemProps) => {
    const {t} = useTranslation();
    const handleShare = () => {
        if (onShared) {
            onShared(savedQuery.id, !savedQuery.shared);
        }
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete(savedQuery.id);
        }
    };

    const getIconTitle = () => {
        return !savedQuery.shared ? t('search.saveSearch.action.shared') : t('search.saveSearch.action.notShared');
    };


    return (
        <Dropdown.Item className={'saved-search-item'}>
            <div className={classNames('saved-search-item-info-wrapper')}>
                <HibuTooltip tooltipKey={savedQuery.name} placement={'auto'} key={savedQuery.name}>
                    <div className="saved-search-item-info-text" onClick={() => onSelect(savedQuery.request)}>
                        <span className={'saved-search-item-link link'}>{savedQuery.name}</span>
                        <div className={'saved-search-item-content'}>
                            <span className={'saved-search-item-date'}>{savedQuery.date}</span>
                            <span className={'saved-search-item-time'}>{savedQuery.time}</span>
                            <span>UTC</span>
                        </div>
                    </div>
                </HibuTooltip>
                {mayShare &&
                    <SvgIcon
                        id="icon-shared-search-shared"
                        className={`saved-search-icon`}
                        icon={Icons.SHARED_ITEM}
                        fading={!savedQuery.shared}
                        onClick={handleShare}
                        alt={getIconTitle()}
                    />
                }
                {editable &&
                    <SvgIcon
                        id="icon-shared-search-delete"
                        className="saved-search-icon"
                        icon={Icons.DELETE}
                        onClick={() => handleDelete()}
                        alt={t('search.saveSearch.remove')}
                    />
                }
            </div>
        </Dropdown.Item>
    );
});
